import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BotoesFlutuantes, BaixarFlutuante, IconeWhatsApp } from '../components/shared/BotoesFlutuantes';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Default company values (fallback)
const DEFAULT_COMPANY = {
  tradeName: 'IPCOM Tecnologia',
  whatsapp: '(45) 3122-5150',
};

/**
 * O PORTAL PÚBLICO DE MATERIAL É UMA VISÃO ÚNICA (decisão do Heitor, 30/09/2026).
 *
 * O documento ocupa a tela inteira e os botões flutuam por cima, no padrão da
 * proposta pública (`ProposalPublicView`): WhatsApp em pílula verde no canto, e
 * Baixar só quando baixa de verdade. A barra do topo saiu: ela cobria o
 * documento e o botão dela ("Baixar", depois "Abrir em nova aba") abria em
 * `financeiro.ipcom.com.br` o mesmo documento que já estava na tela, fora do
 * portal.
 *
 * ⛔ QUANDO O BAIXAR APARECE depende do que a rota do backend FAZ:
 *  - text/html: a rota `download` devolve o HTML INLINE, com CSP sandbox (contra
 *    XSS armazenado, e isso é de propósito), então ELA nunca baixa. Quem baixa é
 *    a rota `/pdf`, que imprime o HTML pelo Chrome do backend e responde
 *    `attachment` (backend #266, no ar em 30/09/2026). Conferido em produção no
 *    MAT000018: 4 páginas A4, o comunicado inteiro.
 *  - application/pdf e image/*: sem parâmetro, a rota redireciona para a URL de
 *    VISUALIZAÇÃO do S3 (é o que o iframe e a <img> usam). Com `?baixar=1`
 *    (backend, pedido ao Bastião em 30/09/2026) redireciona para a URL de
 *    download, com attachment.
 *  - o resto (docx, zip, vídeo…): a rota já manda a URL de download.
 * Como a URL final é do S3, outro domínio, o atributo `download` do <a> seria
 * ignorado: quem decide se baixa é o backend, não o front.
 */
export type BaixarDoArquivo = BaixarFlutuante;

export function baixarDoArquivo(mimeType: string | null | undefined, token: string | undefined): BaixarDoArquivo {
  const rota = `${API_URL}/materials/public/${token}/download`;
  const mime = String(mimeType || '');
  if (mime === 'text/html') return { rotulo: 'Baixar PDF', href: `${API_URL}/materials/public/${token}/pdf` };
  if (mime === 'application/pdf') return { rotulo: 'Baixar PDF', href: `${rota}?baixar=1` };
  if (mime.startsWith('image/')) return { rotulo: 'Baixar imagem', href: `${rota}?baixar=1` };
  return { rotulo: 'Baixar arquivo', href: rota };
}

// Os botões flutuantes moram em components/shared (usados também pela proposta).
export { BotoesFlutuantes };

export default function MaterialPublicView() {
  // Rota /m/t/:token. O `code` sequencial deixou de resolver rota pública para não
  // deixar o catálogo enumerável a partir de um link compartilhado.
  const { token } = useParams<{ token: string }>();
  const [material, setMaterial] = useState<any>(null);
  const [company, setCompany] = useState<any>(DEFAULT_COMPANY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Sem a barra do topo, o título do material fica na aba do navegador.
  useEffect(() => {
    if (!material?.title) return;
    const anterior = document.title;
    document.title = `${material.title} · ${company?.tradeName || DEFAULT_COMPANY.tradeName}`;
    return () => { document.title = anterior; };
  }, [material?.title, company?.tradeName]);

  const loadData = async () => {
    try {
      // Load material and company settings in parallel
      const [materialRes, companyRes] = await Promise.all([
        fetch(`${API_URL}/materials/public/${token}`),
        fetch(`${API_URL}/system/company`),
      ]);

      if (!materialRes.ok) throw new Error('Material não encontrado');
      const materialData = await materialRes.json();
      setMaterial(materialData);

      if (companyRes.ok) {
        const companyData = await companyRes.json();
        setCompany(companyData);
      }
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar material');
    }
    setLoading(false);
  };

  const whatsappNumber = company.whatsapp?.replace(/\D/g, '') || '554531225150';
  const whatsappMessage = encodeURIComponent(
    `Olá! Vi o material "${material?.title || ''}" e gostaria de mais informações.`,
  );
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <div className="text-gray-500">Carregando...</div>
        </div>
      </div>
    );
  }

  if (error || !material) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center bg-white rounded-xl shadow-lg p-8 max-w-md mx-4">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Material não encontrado</h1>
          <p className="text-gray-500 mb-6">
            {error || 'O material solicitado não está disponível ou foi removido.'}
          </p>
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
          >
            <IconeWhatsApp className="w-5 h-5" />
            Fale Conosco
          </a>
        </div>
      </div>
    );
  }

  const isHtml = material.mimeType === 'text/html';
  const isPdf = material.mimeType === 'application/pdf';
  const isImage = material.mimeType?.startsWith('image/');
  const isVideo = material.mimeType?.startsWith('video/');
  const previewUrl = `${API_URL}/materials/public/${token}/download`;
  const baixar = baixarDoArquivo(material.mimeType, token);

  // A resposta de `baixar.href` é attachment: o navegador salva o arquivo e a
  // página do portal fica onde está.
  const handleDownload = () => {
    if (!baixar) return;
    setDownloading(true);
    window.location.href = baixar.href;
    setTimeout(() => setDownloading(false), 2000);
  };

  const botoes = (
    <BotoesFlutuantes baixar={baixar} baixando={downloading} onBaixar={handleDownload} whatsappUrl={whatsappUrl} />
  );

  // HTML e PDF: o documento é a página inteira.
  if (isHtml || isPdf) {
    return (
      <div className="h-screen bg-white">
        <iframe src={previewUrl} className="block w-full h-full border-0" title={material.title} />
        {botoes}
      </div>
    );
  }

  // Imagem, vídeo e o resto: conteúdo centralizado. O pb-32 deixa o fim do
  // conteúdo acima dos botões flutuantes.
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 pt-6 pb-32">
      {isImage && (
        <img
          src={previewUrl}
          alt={material.title}
          className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-sm"
        />
      )}

      {isVideo && (
        <div className="w-full max-w-5xl bg-slate-900 rounded-xl overflow-hidden">
          <video src={previewUrl} controls className="w-full max-h-[85vh]">
            Seu navegador não suporta a reprodução de vídeo.
          </video>
        </div>
      )}

      {!isImage && !isVideo && (
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="text-6xl mb-4">📁</div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">{material.title}</h1>
          {material.description && <p className="text-gray-600 mb-4">{material.description}</p>}
          <p className="text-gray-500 text-sm">{company.tradeName}</p>
        </div>
      )}

      {botoes}
    </div>
  );
}
